<template>
  <div :class="props.mainContainerClasses">
    <div class="min-w-25r min-h-15r relative text-university-primary">
      <!-- Promo Image-->
      <div class="min-h-15r h-full relative overflow-hidden">
        <img
          :data-testid="`card-image-${programPageId}`"
          :src="cardSrc"
          :alt="program_page?.name || ''"
          class="absolute inset-0 h-full w-full object-cover bg-gray-75"
        />
      </div>
      <!-- Program Type(s)-->
      <div
        v-if="displaySessionTypes"
        class="absolute top-1 bg-white inline-block mt-1 ml-2 p-2 rounded-full font-montserrat text-xs font-semibold text-university-primary"
      >
        <div class="flex items-center">
          <template
            v-for="(sessionType, index) in sessionTypesWithIcons"
            :key="index"
          >
            <component
              :is="sessionType?.iconComponentName"
              v-if="!sessionType?.isPlusSign"
              class="mr-[3px]"
            />
            <p v-if="!sessionType?.isPlusSign" class="ml-[3px]">
              {{ sessionType?.name }}
            </p>
            <p v-if="sessionType?.isPlusSign" class="mr-[6px] ml-[3px]">
              +
            </p>
          </template>
        </div>
      </div>
    </div>

    <div class="min-w-25r shadow-md flex flex-col flex-auto">
      <!-- Location & Rec Credits-->
      <div
        class="min-h-8 bg-gray-10 flex justify-between text-sm monts text-university-primary font-medium"
      >
        <div class="text-left pl-2 pt-1">
          <div v-if="programLocation">
            <div class="flex ml-2">
              <LocationIcon class="self-center mr-[6px]" />
              <p class="ml-[3px]">{{ programLocation }}</p>
            </div>
          </div>
        </div>
        <div v-if="creditsAvailable" class="text-right pr-2 pt-1">
          <div class="flex justify-end">
            <CreditsIcon class="inline-block self-center mb-[2px]" />
            <p class="ml-[3px]">{{ creditsAvailable }} Credits</p>
            <div class="ml-[5px] mb-[3px] mt-[2px]">
              <div
                @click="(event) => op.toggle(event)"
                @mouseover.stop="(event) => op.show(event)"
                @mouseleave.stop="(event) => op.hide(event)"
              >
                <AlertCircle
                  :fill="'#007F80'"
                  class="mr-2 text-info-icon small-alert-icon"
                />
              </div>
            </div>
          </div>
          <OverlayPanel
            class="session-card-redesign"
            ref="op"
            :pt="{
              content: { class: 'p-2 bg-white' },
            }"
          >
            <div
              class="grid grid-cols-24-1fr gap-x-1 font-normal normal-case p-2 max-w-sm"
              data-testid="rec-credits-popover"
            >
              <AlertCircle
                :fill="'#007F80'"
                class="mr-2 text-info-icon small-alert-icon"
              />
              <p class="break-normal text-left ml-2">
                Recommended US Semester Credits
              </p>
            </div>
          </OverlayPanel>
        </div>
      </div>
      <!-- Name & Details -->
      <div class="bg-white flex-auto">
        <div
          class="text-university-primary text-xl font-bold ml-4"
          @click="$emit('bookmarkClicked', programPageId)"
        >
          {{ program_page.name }}
        </div>
        <div
          class="grid grid-cols-1"
          :class="{ 'lg:grid-cols-2': !props.verticalStyle }"
        >
          <div
            class="grid grid-cols-24-1fr gap-x-1 gap-y-1 mt-4 ml-4 font-medium text-sm"
          >
            <!-- College / Host University-->
            <SchoolIcon v-if="hostUniversities" />
            <p v-if="hostUniversities" class="ml-[4px]">
              {{ hostUniversities }}
            </p>

            <!-- Term(s) & Duration -->
            <PunchClockIcon v-if="displaySessionTerms" />
            <p v-if="displaySessionTerms" class="ml-[4px]">
              <span class="inline-block">
                {{ displayNameForTerms }}
              </span>
              <span v-if="formattedDuration" class="inline-block"
                >, {{ formattedDuration }}
              </span>
            </p>

            <!-- Start + End dates-->
            <DateRangeIcon class="ml-[3px]" />
            <ProgramDates
              :id="`program-dates-${programPageId + session.id}`"
              :data-testid="`program-dates-${programPageId + session.id}`"
              :dates="programDates"
              :start-is-final="isStartDateFinal"
              :end-is-final="isEndDateFinal"
              tag="p"
              class="ml-[3px]"
              reverse-icon-order
              experience-card-icon
              text-class="font-montserrat text-university-primary text-sm"
            />

            <!-- Application Deadline -->
            <DeadlineIcon />
            <div class="text-university-primary">
              <p class="ml-[3px] uppercase font-bold inline">
                Application Deadline:
              </p>
              <p
                :class="{ 'lg:block': !props.verticalStyle }"
                class="ml-[3px] inline uppercase font-bold"
              >
                {{ applicationDeadline }}
                <span
                  v-if="!applicationDeadlineNotPassed"
                  @click="(event) => op3.toggle(event)"
                  @mouseover.stop="(event) => op3.show(event)"
                  @mouseleave.stop="(event) => op3.hide(event)"
                >
                  <span
                    :id="`icon-${programPageId}-${session?.id}-application-deadline`"
                    class="material-symbols-outlined mr-2 text-warning-800 text-lg leading-none inline"
                    aria-hidden="true"
                    >error</span
                  >
                </span>
                <OverlayPanel
                  class="session-card-redesign"
                  ref="op3"
                  :pt="{ content: { class: 'p-2 bg-white' } }"
                >
                  <div
                    class="grid grid-cols-24-1fr gap-x-1 font-normal normal-case p-2"
                    data-testid="app-deadline-popover"
                  >
                    <AlertCircle
                      class="mr-2 text-warning-800"
                      :fill="'#F96F43'"
                    />
                    <p class="break-normal text-left ml-2">
                      Deadline has passed and applications are closed.
                    </p>
                  </div>
                </OverlayPanel>
              </p>
            </div>
          </div>
          <div
            :class="{ 'lg:mt-4': !props.verticalStyle }"
            class="grid grid-cols-24-1fr gap-x-1 gap-y-1 ml-4 mb-4 font-medium text-sm"
          >
            <!-- Areas of study-->
            <PsychologyIcon v-if="areaOfStudy" />
            <div v-if="areaOfStudy" class="ml-[3px]">
              <p>
                {{ areasOfStudyLabel }}
                <span
                  v-if="summarizedAreaOfStudy.itemsExceedingLimit.length"
                  :id="`icon-${programPageId}-${session?.id}-areas-of-study`"
                  :class="`${secondaryColorClass}`"
                  @click="(event) => op2.toggle(event)"
                  @mouseover.stop="(event) => op2.show(event)"
                  @mouseleave.stop="(event) => op2.hide(event)"
                >
                  +
                  {{ summarizedAreaOfStudy.itemsExceedingLimit.length }}
                  more</span
                >
              </p>
              <OverlayPanel
                v-if="summarizedAreaOfStudy.itemsExceedingLimit.length"
                class="session-card-redesign"
                ref="op2"
                :pt="{ content: { class: 'p-2 bg-white' } }"
              >
                <p class="p-4 max-w-sm">
                  {{ areasOfStudyPopUpContent }}
                </p>
              </OverlayPanel>
            </div>

            <!-- Price-->
            <MoneyIcon v-if="showPrice" />
            <p
              v-if="showPrice"
              class="font-bold ml-[3px] uppercase text-university-primary"
            >
              Starting At {{ priceInDollars }}
            </p>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="bg-gray-100 text-university-primary">
        <div class="grid grid-cols-2 justify-items-center items-center">
          <!-- Apply -->
          <div>
            <div
              v-if="applicationDeadlineNotPassed"
              :id="`apply-button-${programPageId}`"
            >
              <div class="md:flex md:items-center md:justify-center">
                <button
                  class="flex justify-center items-center text-sm font-bold uppercase cursor-pointer"
                  :class="
                    secondaryColor
                      ? `${secondaryColorClass}`
                      : `text-university-secondary`
                  "
                  :aria-label="`Apply to ${program_page.name} .`"
                  @click.stop="$emit('applyClicked', props.sessionData)"
                >
                  <span class="leading-none uppecase">{{
                    applyButtonDescription
                  }}</span>
                  <ArrowRight :stroke-width="'4'" custom-class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- View -->
          <div>
            <ApplyButton
              :id="`view-button-${programPageId}`"
              data-testid="view-button"
              type="button"
              class="bg-transparent w-32 h-14 mt-2 mb-4"
              :outlined="true"
              :event-category="'view-program-page'"
              @click="$emit('viewClicked')"
            >
              <span
                class="font-semibold uppercase whitespace-nowrap leading-none"
              >
                View
              </span>
              <ArrowRight :stroke-width="'4'" custom-class="w-4 h-4" />
            </ApplyButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import OverlayPanel from "primevue/overlaypanel";
import { computed, ref, unref } from "vue";
import { useStore } from "vuex";
import { useApplicationProgress } from "@/components/ExperiencePage/Algolia/composables.js";
import {
  getCreditsAvailable,
  getDisplayNameForTerms,
  getFormattedDuration,
  getHostUniversities,
  getIsEndDateFinal,
  getIsStartDateFinal,
  getProgramDates,
  getProgramLocationTerm,
  getSessionTerms,
} from "@/components/ExperiencePage/Algolia/ResultCards/CardContent/utils.js";
import {
  assignIconsToSessionTypes,
  cardImage,
  getAreaOfStudy,
  getPriceInDollars,
  segmentByCharacterLength,
} from "@/components/ExperiencePage/Algolia/ResultCards/utils.js";
import ProgramDates from "@/components/ProgramDates";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import AlertCircle from "@/components/shared/icons/AlertCircle.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import BriefCaseIcon from "@/components/shared/icons/BriefCaseIcon.vue";
import CareerExplorationIcon from "@/components/shared/icons/CareerExplorationIcon.vue";
import CreditsIcon from "@/components/shared/icons/CreditsIcon.vue";
import DateRangeIcon from "@/components/shared/icons/DateRangeIcon.vue";
import DeadlineIcon from "@/components/shared/icons/DeadlineIcon.vue";
import ExchangeIcon from "@/components/shared/icons/ExchangeIcon.vue";
import FacultyLedIcon from "@/components/shared/icons/FacultyLedIcon.vue";
import FellowshipIcon from "@/components/shared/icons/FellowshipIcon.vue";
import LocationIcon from "@/components/shared/icons/LocationIcon.vue";
import MoneyIcon from "@/components/shared/icons/MoneyIcon.vue";
import PsychologyIcon from "@/components/shared/icons/PsychologyIcon.vue";
import PunchClockIcon from "@/components/shared/icons/PunchClockIcon.vue";
import TeachingEnglishIcon from "@/components/shared/icons/TeachingEnglishIcon";
import VirtualSessionTypeIcon from "@/components/shared/icons/VirtualSessionTypeIcon.vue";
import VolunteerIcon from "@/components/shared/icons/VolunteerIcon.vue";
import WorldIcon from "@/components/shared/icons/WorldIcon.vue";
import SchoolIcon from "@/components/svg-icons/SchoolIcon.vue";
import { hasConfigurableRegistrationEnabled } from "@/composables/authorization.js";
import { AREAS_OF_STUDY_CHARACTER_LIMIT, SESSION_TYPE } from "@/constants.js";
import { formatDate } from "@/mixins/helpers";
import { endOfLocalDay, isPastDate } from "@/util/pastSessionDeadline";
import { hidePrice } from "@/util/pricingLabel";

defineEmits(["bookmarkClicked", "viewClicked", "applyClicked"]);

const props = defineProps({
  mainContainerClasses: {
    type: String,
    default: "",
  },
  verticalStyle: {
    type: Boolean,
    default: false,
  },
  imageUrl: {
    type: String,
    default: "",
  },
  sessionData: {
    type: Object,
    default: () => ({}),
  },
});

defineOptions({
  name: "ProgramPageCardRedesign",
});

const MAP_ICON_COMPONENTS_WITH_SESSION_TYPES = {
  // Session Type: Vue name of component
  [SESSION_TYPE.study.name]: WorldIcon,
  [SESSION_TYPE.internship.name]: BriefCaseIcon,
  [SESSION_TYPE.fellowship.name]: FellowshipIcon,
  [SESSION_TYPE.facultyLed.name]: FacultyLedIcon,
  [SESSION_TYPE.careerExploration.name]: CareerExplorationIcon,
  [SESSION_TYPE.research.name]: BriefCaseIcon,
  [SESSION_TYPE.virtual.name]: VirtualSessionTypeIcon,
  [SESSION_TYPE.teachingEnglish.name]: TeachingEnglishIcon,
  [SESSION_TYPE.exchange.name]: ExchangeIcon,
  [SESSION_TYPE.volunteer.name]: VolunteerIcon,
};

const store = useStore();
const { getRegisterType } = useApplicationProgress();
const program_page = computed(() => unref(props.sessionData)?.program_page);
const programPageId = computed(() => unref(program_page)?.id);
const session = computed(() => unref(props.sessionData)?.session);
const programLocation = computed(() => getProgramLocationTerm(unref(session)));
const hostUniversities = computed(() => getHostUniversities(unref(session)));
const sessionTerms = computed(() => getSessionTerms(unref(session)));
const displayNameForTerms = computed(() => getDisplayNameForTerms(unref(sessionTerms), " + "));
const formattedDuration = computed(() => getFormattedDuration(unref(session), false));
const programDates = computed(() => getProgramDates(unref(session)));
const isStartDateFinal = computed(() => getIsStartDateFinal(unref(session)));
const isEndDateFinal = computed(() => getIsEndDateFinal(unref(session)));
const areaOfStudy = computed(() => {
  return getAreaOfStudy(unref(session)?.subject_area_sublevels || [], false);
});
const applyButtonDescription = computed(() =>
  !hasConfigurableRegistrationEnabled.value ? "Personalize & Apply" : getRegisterType(session),
);

const summarizedAreaOfStudy = computed(() => {
  return segmentByCharacterLength(areaOfStudy.value.split(", "), AREAS_OF_STUDY_CHARACTER_LIMIT);
});

const areasOfStudyLabel = computed(() => summarizedAreaOfStudy.value?.itemsWithinLimit.join(", "));
const areasOfStudyPopUpContent = computed(() => summarizedAreaOfStudy.value?.itemsExceedingLimit.join(", "));

const applicationDeadline = computed(() =>
  formatDate(new Date(unref(session).application_deadline).toISOString(), "short"),
);

const applicationDeadlineNotPassed = computed(() => {
  return !isPastDate(endOfLocalDay(unref(session).application_deadline));
});

const creditsAvailable = computed(() => {
  return getCreditsAvailable(unref(session)?.academic_min_credits ?? 0, unref(session)?.academic_max_credits ?? 0);
});

const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");
const priceInDollars = computed(() => getPriceInDollars(unref(props.sessionData), unref(homeInstitutionId)));
const billingRules = computed(() => store.state.billingRules);
const showPrice = computed(() => !hidePrice(unref(session), billingRules.value));
const cardSrc = computed(() => cardImage(props.imageUrl, props.sessionData));
const sessionTypesWithIcons = computed(() =>
  assignIconsToSessionTypes(session.value?.session_types, MAP_ICON_COMPONENTS_WITH_SESSION_TYPES),
);
const displaySessionTypes = computed(() => sessionTypesWithIcons.value.length);
const displaySessionTerms = computed(() => {
  return Array.isArray(sessionTerms.value) && sessionTerms.value.length >= 1;
});

// Overlay panel refs
const op = ref();
const op2 = ref();
const op3 = ref();
</script>

<style scoped>
.basis-1\/2 {
  flex-basis: 50%;
}
</style>
